import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {
  getSalesTargetCsvFile,
  addDraftSalesTargetList,
  updateDraftSalesTargetList,
  addSalesTargetList,
  updateEditSalesTargetTemplate,
  validateSalesTargetName
} from '@/services/salesTargets';
import {getUUID, handleNameValidationViaCallbackFn} from '@/utils/commons';
import {useState} from 'react';
import {
  createSalesTargetRequestObject,
  tranformRuleSetOfSalesTarget
} from '@/pages/SalesTargetUtils';
import {useAppDispatch, useAppSelector} from '@/store';
import {resetSalesTarget} from '@/store/slices/salesTargetSlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {Constants, STATUS_VALUES} from '@/utils/constants/commonConstants';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {logger} from '@/helpers/logger';
import {
  formatDataForStep,
  shouldTransformRuleSetOfSalesTarget,
  getDisplayContent,
  formatDraftNotificationMessage,
  createReqObj,
  createPreReqReqObj
} from '@/hooks/useSalesTargetsForm';

/**
 * Hook for sales target mutations, CSV download, validation, and toasts.
 * @param {Object} options
 * @param {Array} options.searchArr - Current search filters (from useSalesTargetsQuery)
 * @param {string} options.queryParams - Current query params string (from useSalesTargetsQuery)
 */
export const useSalesTargetsActions = ({
  searchArr = [],
  queryParams = ''
} = {}) => {
  const dispatch = useAppDispatch();
  const countryCode = useAppSelector(selectCountryCode);
  const [isNameValid, setIsNameValid] = useState(true);

  const handleValidationModalClose = () => {
    setIsNameValid(true);
  };

  // Helper function to show toast notifications
  const showToast = (message, type = 'info') => {
    dispatch(setToastNotifications({show: true, message, type}));
  };

  const onDownloadBtnClick = async (selectedIds, excludeIds = []) => {
    const body = {
      ids: selectedIds,
      excludeIds,
      filters: searchArr
    };

    // Get language codes based on selected country
    const countryLanguages = COUNTRY_LANGUAGES_CODES[countryCode] || [];
    const languageCodes = countryLanguages.map((lang) => lang.code).join(',');

    // Append language codes to query params
    const languageParams = languageCodes ? `&languages=${languageCodes}` : '';
    const updatedQueryParams = queryParams
      ? `${queryParams}${languageParams}`
      : `?languages=${languageCodes}`;

    const data = await getSalesTargetCsvFile(updatedQueryParams, body);
    const fileName = `SalesTarget_${getDateWithTimeString()}`;
    downloadCSVFiles(data, fileName);
  };

  const handleNameValidation = async (queryParamsObj) => {
    const data = await handleNameValidationViaCallbackFn(
      validateSalesTargetName,
      queryParamsObj
    );
    return data;
  };

  const handleDraft = async (
    formData,
    salesTarget,
    isPrerequisiteTarget,
    step = 'setup',
    additionalData = {}
  ) => {
    const hasExistingUuid = salesTarget.uuid !== null;
    const data = {...formData};

    // Format data based on step
    const formattedData = formatDataForStep(data, step);

    // Handle step-specific logic
    switch (step) {
      case 'setup':
        formattedData.numberLevel = formattedData.numberLevel || null;
        formattedData.numberMileStone = formattedData.numberMileStone || null;
        break;
      case 'displayContent':
        formattedData.displayContent = getDisplayContent(
          isPrerequisiteTarget,
          additionalData
        );
        break;
      case 'products':
        break;
      case 'review':
        break;
      default:
        break;
    }

    let requestData = createSalesTargetRequestObject(
      formattedData,
      isPrerequisiteTarget
    );
    requestData.uuid = hasExistingUuid
      ? salesTarget.uuid
      : getUUID(formattedData?.targetName);
    requestData.status = Constants.Draft;

    // Apply tranformRuleSetOfSalesTarget for products step
    if (
      shouldTransformRuleSetOfSalesTarget(step) &&
      additionalData.categoryListData
    ) {
      requestData = tranformRuleSetOfSalesTarget(
        requestData,
        additionalData.categoryListData
      );
    }

    try {
      let response;
      if (hasExistingUuid) {
        response = await updateDraftSalesTargetList({data: requestData});
      } else {
        response = await addDraftSalesTargetList({data: requestData});
      }

      if (response.success) {
        dispatch(resetSalesTarget());
        dispatch(
          setToastNotifications({
            show: true,
            message: formatDraftNotificationMessage(hasExistingUuid),
            type: 'info'
          })
        );
      } else {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'New Sales Target not Drafted',
            type: 'error'
          })
        );
      }

      return response;
    } catch (error) {
      logger.error('Error in handleDraft:', error);
      dispatch(
        setToastNotifications({
          show: true,
          message: 'New Sales Target not Drafted',
          type: 'error'
        })
      );
      throw error;
    }
  };

  const handlePublish = async (
    salesTargetData,
    isPrerequisiteTarget,
    categoryList,
    navigate,
    ROUTES
  ) => {
    const hasExistingUuid = salesTargetData.uuid !== null;
    const reqObj = isPrerequisiteTarget
      ? await createPreReqReqObj(salesTargetData, categoryList)
      : await createReqObj(salesTargetData, categoryList);
    reqObj['uuid'] = hasExistingUuid
      ? salesTargetData.uuid
      : getUUID(reqObj.targetName);
    reqObj.status = STATUS_VALUES.Published;
    try {
      const response = await addSalesTargetList({data: reqObj});
      if (Object.keys(response).length > 0) {
        if (response?.success === false) {
          const errMsg =
            response?.message ||
            'An unknown error occurred while publishing sales target';
          dispatch(
            setToastNotifications({show: true, message: errMsg, type: 'error'})
          );
        } else if (
          (response?.success === undefined || response?.success === null) &&
          response?.status === STATUS_VALUES.Published
        ) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'New Sales Target published',
              type: 'success'
            })
          );
          dispatch(resetSalesTarget());
          navigate(ROUTES.SALES_TARGETS);
        } else {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'New Sales Target published',
              type: 'success'
            })
          );
          dispatch(resetSalesTarget());
          navigate(ROUTES.SALES_TARGETS);
        }
      } else
        dispatch(
          setToastNotifications({
            show: true,
            message: 'New Sales Target not published',
            type: 'error'
          })
        );
    } catch {
      const errMsg =
        'An unknown error occurred while publishing sales target. Please verify the data and try again.';
      dispatch(
        setToastNotifications({show: true, message: errMsg, type: 'error'})
      );
    }
  };

  const onTemplateUpdate = async (id, requestData) => {
    try {
      let response = await updateEditSalesTargetTemplate(id, {
        fileUrl: requestData
      });
      if (response?.success) {
        showToast('Template Updated Successfully', 'success');
        return true;
      } else {
        showToast('Template Update Failed', 'error');
        return false;
      }
    } catch (error) {
      logger.error('Error in onTemplateUpdate:', error);
      showToast('Template Update Failed', 'error');
      return false;
    }
  };

  return {
    onDownloadBtnClick,
    handleDraft,
    handlePublish,
    handleNameValidation,
    isNameValid,
    setIsNameValid,
    handleValidationModalClose,
    onTemplateUpdate
  };
};
